"use client";

import React, { useState } from 'react';
import { Button } from './button';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { Loader2 } from 'lucide-react';

interface TermsAndConditionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function TermsAndConditionsModal({ isOpen, onClose }: TermsAndConditionsModalProps) {
  const { acceptTermsAndConditions, isLoading, user } = useAuth();
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();

  const handleAccept = async () => {
    setIsAccepting(true);
    setError(null);

    try {
      const result = await acceptTermsAndConditions();
      if (result.success) {
        onClose();
        // Redirect based on user role after accepting terms
        if (user) {
          if (user.role === "Principal") {
            router.push("admin/dashboard");
          } else {
            router.push("student/dashboard");
          }
        }
      } else {
        setError(result.error || 'Failed to accept terms and conditions');
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsAccepting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-fadeIn border-t-6 border-aralin-green">
        {/* Header */}
        <div className="flex justify-center items-center p-5 border-b">
          <h2 className="text-3xl font-semibold text-center text-gray-900 select-none">Privacy Notice</h2>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-12">
          <div className="space-y-6 text-gray-700">
            <p>
              <strong>Effective Date:</strong> January 1, 2025
            </p>

            <h3 className="text-xl font-semibold text-aralin-green mt-6 mb-3">1. Purpose of this Privacy Notice</h3>
            <p>
              Aralin Insights values your privacy. This Privacy Notice explains what personal data we collect, why we collect it, and how it is processed in line with the Data Privacy Act of 2012, our Data Sharing Agreement with your school, and the Aralin Data Privacy Manual.
              <br /> <br />
              By clicking &quot;I Agree,&quot; you confirm that you have read and understood this Privacy Notice and consent to the processing of your personal data as described below.
            </p>

            <h3 className="text-xl font-semibold text-aralin-green mt-6 mb-3">2. Personal Data We Collect</h3>
            <p>
              We only collect the minimum personal data necessary to deliver our services:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Full Name</li>
              <li>Grade Level</li>
              <li>Section</li>
            </ul>
            <p>
              No other personal data (such as contact details, demographics, or sensitive information) is required for your use of Aralin Insights.
            </p>

            <h3 className="text-xl font-semibold text-aralin-green mt-6 mb-3">3. How We Use Your Data</h3>
            <p>
              Your personal data is used only for educational purposes, specifically:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>To generate your login credentials.</li>
              <li>To properly tag your exam results and learning data under your name, section, and grade level.</li>
              <li>To provide individual and school-wide analytics, enabling teachers and administrators to understand performance trends and learning needs.</li>
              <li>To support aggregated data reports for your school&apos;s use in monitoring and improving learning outcomes.</li>
            </ul>
            <p>We do not sell, share, or repurpose your personal data for marketing or unrelated purposes.</p>

            <h3 className="text-xl font-semibold text-aralin-green mt-6 mb-3">4. Data Sharing</h3>
            <p>
              Your data remains the property of your school. It is only shared under the scope of the Data Sharing Agreement with your school.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Within Aralin:</strong>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>Access is role-based and limited to authorized personnel.</li>
                </ul>
              </li>
              <li><strong>With Third-Party Providers:</strong>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>AWS Lightsail (Singapore) stores account credentials securely.</li>
                  <li>OpenAI processes anonymized answer data for analytics only. No names, emails, or identifiers are sent.</li>
                </ul>
              </li>
            </ul>

            <h4 className="text-lg font-semibold text-aralin-green mt-6 mb-3 ml-4">Data Ownership</h4>
            <div className="ml-4">
              <p>
                All personal data collected through Aralin Insights, including names, grade levels, sections, and any related performance analytics, shall remain the property of the school that provided access to the platform.
              </p>
              <br />
              <p>
                Aralin acts only as a processor of such data, performing tagging, analytics, and reporting strictly on behalf of the school and for the educational purposes defined in the Data Sharing Agreement.
              </p>
              <br />
              <p><strong>
                By agreeing to this Privacy Notice, you acknowledge that:
              </strong>
              </p>
              <br />
              <ul className="list-disc pl-6 space-y-2">
                <li>The school is the lawful owner and controller of your data.</li>
                <li>Aralin does not claim any ownership rights over your personal data.</li>
                <li>Aralin will not use or disclose your data beyond the purposes expressly authorized by the school and the Data Privacy Act of 2012.</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-aralin-green mt-6 mb-3">5. Data Retention and Disposal</h3>
            <p>
              Your data will be kept only for as long as necessary to fulfill the purposes above, typically two academic years from your last activity. After this period, it will be securely deleted unless your school requests retention for academic tracking.
            </p>

            <h3 className="text-xl font-semibold text-aralin-green mt-6 mb-3">6. Your Rights as a Data Subject</h3>
            <p>
            Under the Data Privacy Act, you have the right to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Be informed of how your data is used.</li>
              <li>Access, correct, or request deletion of your data.</li>
              <li>Object to processing in certain cases.</li>
              <li>File complaints with the National Privacy Commission.</li>
            </ul>
            <p>All requests may be directed to Aralin&apos;s Data Protection Officer (DPO).</p>

            <h3 className="text-xl font-semibold text-aralin-green mt-6 mb-3">7. Contact Information</h3>
            <p>
            For any questions, concerns, or requests regarding your data, please contact: <br />

            <strong>Data Protection Officer (DPO)
            <br />
            Ana Katrina P. De Jesus
            <br />
            Email: dataprotection@aralinonline.com
            </strong>
            </p>

            <h3 className="text-xl font-semibold text-aralin-green mt-6 mb-3">8. Consent</h3>
            <p>
            By clicking &quot;I Agree,&quot; you confirm that:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>You have read and understood this Privacy Notice.</li>
              <li>You consent to the collection and processing of your name, grade level, and section for the purposes stated above.</li>
              <li>You understand that your school remains the owner of your data, and Aralin acts only as a data processor under the agreed purposes.</li>
            </ul>
          </div>
        </div>

        {/* Footer with Action Buttons */}
        <div className="border-t p-5">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-4 mb-6">
              <div className="flex items-start space-x-2">
                <div className="flex-shrink-0 mt-0.5">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button
              onClick={handleAccept}
              disabled={isAccepting || isLoading}
              className="bg-aralin-green hover:bg-aralin-green-dark text-white"
            >
              {isAccepting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Accepting...
                </>
              ) : (
                "I Agree"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
